import { scene } from "./index";
import { curveToPoints, brepToGeometry, faceToGeometry } from "./rhinoToThree";
import * as THREE from "three";
import verb from "verb-nurbs-web";
import { FaceList } from "../modules";
import { isPoint } from "../utils";

function addValuesToGroup(group, values) {
  values.forEach((v) => {
    if (Array.isArray(v) && !isPoint(v)) {
      return addValuesToGroup(group, v);
    } else {
      const mesh = toMesh(v);
      if (mesh) {
        group.add(mesh);
      }
    }
  });
}

export function addToScene(id, value) {
  if (!value) {
    const o = scene.getObjectByName(id);
    if (o) {
      scene.remove(o);
    }
  }

  const isArray = Array.isArray(value);

  if (isArray && !isPoint(value)) {
    const group = new THREE.Group();
    group.name = id;

    const o = scene.getObjectByName(id);
    if (o) {
      group.visible = o.visible;
      scene.remove(o);
    }
    addValuesToGroup(group, value);
    scene.add(group);
  } else {
    const mesh = toMesh(value);

    if (mesh) {
      mesh.name = id;

      const o = scene.getObjectByName(id);
      if (o) {
        mesh.visible = o.visible;
        o.geometry = mesh;
        scene.remove(o);
      }

      scene.add(mesh);
    }
  }
}

export function toGeometry(object) {
  if (object instanceof FaceList) {
    return object.getAll().map((face) => faceToGeometry(face));
  }

  if (object instanceof window.rhino.Brep) {
    return brepToGeometry(object);
  }

  if (object instanceof window.rhino.NurbsSurface) {
    return faceToGeometry(object);
  }

  if (object instanceof window.rhino.NurbsCurve) {
    const curvePoints = curveToPoints(object, 100);
    const vectors = curvePoints.map(
      (point) => new THREE.Vector3(point[0], point[1], point[2])
    );
    const curve = new THREE.CatmullRomCurve3(vectors);
    const points = curve.getPoints(100);
    return new THREE.BufferGeometry().setFromPoints(points);
  }

  if (object instanceof verb.geom.NurbsCurve) {
    var geometry = new THREE.BufferGeometry();
    const tesselated = object
      .tessellate()
      .map((point) => new THREE.Vector3(point[0], point[1], point[2]));

    geometry.setFromPoints(tesselated);

    return geometry;
  }

  if (object instanceof verb.geom.NurbsSurface) {
    const tess = object.tessellate();

    const geometry = new THREE.BufferGeometry();

    geometry.setIndex(tess.faces.flat());
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(tess.points.flat(), 3)
    );
    geometry.setAttribute(
      "normal",
      new THREE.Float32BufferAttribute(tess.normals.flat(), 3)
    );

    return geometry;
  }

  if (isPoint(object)) {
    const vertices = [];
    vertices.push(object[0], object[1], object[2]);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    return geometry;
  }
}

export default function toMesh(object) {
  if (!object) return null;

  const materialOptions = {
    color: 0xff0000,
    opacity: 0.7,
    transparent: true,
    side: THREE.DoubleSide,
  };

  const pointsMaterial = new THREE.PointsMaterial({
    ...materialOptions,
    size: 8,
    sizeAttenuation: false,
  });
  const lineMaterial = new THREE.LineBasicMaterial(materialOptions);
  const meshMaterial = new THREE.MeshPhongMaterial(materialOptions);

  if (
    object instanceof THREE.Mesh ||
    object instanceof THREE.Points ||
    object instanceof THREE.Line
  ) {
    return object;
  }

  if (object instanceof FaceList) {
    const geometry = toGeometry(object);

    const group = new THREE.Group();

    geometry.forEach((geom) => {
      const object = new THREE.Mesh(geom, meshMaterial);
      group.add(object);
    });

    return group;
  }

  if (object instanceof window.rhino.Brep) {
    const geometry = toGeometry(object);

    const group = new THREE.Group();

    geometry.forEach((geom) => {
      const object = new THREE.Mesh(geom, meshMaterial);
      group.add(object);
    });

    return group;
  }

  if (object instanceof verb.geom.NurbsCurve) {
    const geometry = toGeometry(object);
    return new THREE.Line(geometry, lineMaterial);
  }

  if (object instanceof window.rhino.NurbsSurface) {
    const geometry = faceToGeometry(object);
    return new THREE.Mesh(geometry, meshMaterial);
  }

  if (object instanceof window.rhino.NurbsCurve) {
    const geometry = toGeometry(object);
    return new THREE.Line(geometry, lineMaterial);
  }

  if (object instanceof verb.geom.NurbsSurface) {
    const geometry = toGeometry(object);
    return new THREE.Mesh(geometry, meshMaterial);
  }

  if (isPoint(object)) {
    const geometry = toGeometry(object);
    return new THREE.Points(geometry, pointsMaterial);
  }
}
